import { motion } from "framer-motion";
import { Phone, Sparkles, Gift, ShieldCheck, Clock, MapPin, Languages, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";
import LeadFormEmbed from "@/components/LeadFormEmbed";
import { business } from "@/lib/business-data";
import { imgSize } from "@/lib/image-dimensions";

const facts = [
  { icon: Clock, label: "Mon – Sat, 7am – 7pm" },
  { icon: MapPin, label: "30-Mile Service Radius" },
  { icon: ShieldCheck, label: "Free Written Estimates" },
  { icon: Languages, label: "English & Spanish" },
];

const HeroSection = () => {
  const { openQuoteForm, isQuoteFormOpen } = useQuoteForm();

  return (
    <section id="home" className="hero-shell hero-cinematic">
      <img
        src="/assets/images/hero-home.webp"
        alt={`${business.name} craftsman completing a home repair in ${business.address.city}, ${business.address.stateShort}`}
        className="absolute inset-0 w-full h-full object-cover animate-bg-zoom"
        loading="eager"
        fetchPriority="high"
        decoding="async"
        {...imgSize("/assets/images/hero-home.webp")}
      />

      <motion.div
        aria-hidden="true"
        className="absolute top-24 right-16 w-96 h-96 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.35), transparent 70%)" }}
        animate={{ scale: [1, 1.1, 1], opacity: [0.2, 0.35, 0.2] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden="true"
        className="absolute bottom-32 left-8 w-72 h-72 rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, hsl(var(--secondary) / 0.3), transparent 70%)" }}
        animate={{ scale: [1, 1.15, 1], opacity: [0.12, 0.26, 0.12] }}
        transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", delay: 2 }}
      />

      <div className="relative z-10 container-max px-4 w-full py-28 md:py-32 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10 items-center">
          {/* Left: copy, left-aligned */}
          <div className="lg:col-span-7 text-left">
            <motion.div
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <motion.span
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.15, duration: 0.5 }}
                className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/25 text-white text-xs sm:text-sm font-bold uppercase tracking-[0.18em] px-4 py-2 mb-7"
              >
                <Sparkles className="h-3.5 w-3.5" style={{ color: "hsl(var(--secondary-on-dark))" }} />
                Serving {business.address.city} & surrounding areas
              </motion.span>

              <h1
                className="font-heading text-white font-bold tracking-tight mb-6"
                style={{ fontSize: "clamp(2.5rem, 6vw, 4.75rem)", lineHeight: 1.02 }}
              >
                {business.address.city}'s Handyman —
                <br />
                Repair, Install,{" "}
                <span className="relative inline-block">
                  <span style={{ color: "hsl(var(--secondary-on-dark))" }}>Improve</span>
                  <span
                    aria-hidden="true"
                    className="absolute left-0 right-0 -bottom-1.5 h-[6px] rounded-full"
                    style={{
                      background:
                        "linear-gradient(90deg, hsl(var(--secondary-on-dark)) 0%, hsl(var(--primary-on-dark)) 100%)",
                      opacity: 0.85,
                    }}
                  />
                </span>
              </h1>

              {/* Tagline — solid white on the dark cinematic overlay, with a
                  text shadow so it stays readable over any part of the image. */}
              <p
                className="text-white text-base sm:text-lg md:text-xl mb-9 leading-relaxed max-w-2xl"
                style={{ textShadow: "0 2px 14px rgba(0,0,0,0.65)" }}
              >
                Drywall, painting, plumbing, electrical, carpentry, doors, and fans — handled by
                one local crew that shows up when promised and cleans up before it leaves.
              </p>

              <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4 mb-9">
                <Button
                  size="lg"
                  onClick={openQuoteForm}
                  className="text-base gap-2 px-8 py-6 sm:text-lg rounded-xl btn-shine shadow-[0_18px_40px_-12px_hsl(var(--primary)/0.75)]"
                >
                  Get Free Estimate <ArrowRight className="h-5 w-5" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  asChild
                  className="text-base gap-2 px-8 py-6 sm:text-lg rounded-xl border-2 border-white/35 text-white bg-white/10 hover:bg-white/20 hover:text-white hover:border-white/60 backdrop-blur-sm"
                >
                  <a href={business.phoneHref} aria-label={`Call ${business.name} at ${business.phone}`}>
                    <Phone className="h-5 w-5" /> {business.phone}
                  </a>
                </Button>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.5 }}
                className="inline-flex items-center gap-3 rounded-xl border border-white/20 bg-white/10 backdrop-blur-md px-5 py-3 mb-9"
              >
                <Gift className="h-5 w-5 shrink-0" style={{ color: "hsl(var(--secondary-on-dark))" }} />
                <span className="text-white font-semibold text-sm">
                  {business.offers.returning.short} ·{" "}
                  <span style={{ color: "hsl(var(--secondary-on-dark))" }}>
                    {business.offers.referral.short}
                  </span>
                </span>
              </motion.div>

              <div className="flex flex-wrap gap-x-7 gap-y-3">
                {facts.map(({ icon: Icon, label }, i) => (
                  <motion.div
                    key={label}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.65 + i * 0.08, duration: 0.4 }}
                    className="flex items-center gap-2"
                  >
                    <Icon className="h-4 w-4 shrink-0" style={{ color: "hsl(var(--primary-on-dark))" }} />
                    <span className="text-white/90 text-xs sm:text-sm font-semibold uppercase tracking-wider">
                      {label}
                    </span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Right: the lead form widget, at its own natural size.
              Unmounted while the popup is open so only one instance of the
              widget iframe exists in the document at a time. */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.35, duration: 0.7 }}
            className="lg:col-span-5 relative flex justify-center lg:justify-end"
          >
            <div
              aria-hidden="true"
              className="absolute -inset-6 rounded-[2rem] opacity-70 blur-2xl pointer-events-none"
              style={{ background: "radial-gradient(60% 60% at 50% 50%, hsl(var(--primary) / 0.5), transparent 70%)" }}
            />
            <div className="relative w-full max-w-[520px] rounded-2xl bg-card shadow-premium-lg border border-white/20">
              <div className="px-5 pt-6 pb-4 sm:px-6 sm:pt-7">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 border border-primary/25 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-primary mb-3">
                  <Sparkles className="h-3 w-3" /> Free Estimate
                </span>
                <h2 className="font-heading text-2xl font-bold text-foreground">Get Your Free Estimate</h2>
                <p className="text-muted-foreground text-sm mt-1.5">
                  Tell us about the job and we'll get back to you with clear next steps.
                </p>
              </div>
              <div className="px-5 pb-6 sm:px-6">
                {!isQuoteFormOpen && <LeadFormEmbed />}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="hero-curve" aria-hidden="true" />
    </section>
  );
};

export default HeroSection;
